import React, { useState, useRef, useCallback, useEffect } from "react";
import { api, apiFetch } from "../services/api";
import { useAiJobState } from "../hooks/useAiJobState";
import {
  beginOptimisticEnrichmentQueue,
  finalizeOptimisticEnrichmentQueue,
  finalizeOptimisticEnrichmentQueueOnTimeout,
  isEnrichmentQueueTimeoutError,
  rollbackOptimisticEnrichmentQueue,
} from "../utils/enrichmentClientCache";
import { createDevConsole } from "../utils/devLogger";
import {
  isLlmQuotaExhausted,
  LLM_QUOTA_EXHAUSTED_MESSAGE,
} from "../utils/llmQuota";
import {
  addKnowledgeQueueLabel,
  addKnowledgeSuccessMessage,
  queueAddKnowledgeJob,
} from "../utils/addKnowledgeQueue";

const console = createDevConsole();
const debugLog = (...args) => console.log(...args);
const debugWarn = (...args) => console.warn(...args);
const debugError = (...args) => console.error(...args);

const MultiFileUpload = ({
  onFilesProcessed,
  onError,
  disabled = false,
  onNavigateToInbox,
  onBatchProcessingStarted,
  trialStatus = null,
  /** When set (Add knowledge), ingestion is queued against this anchor. */
  knowledgeAnchor = null,
  anchorApplicationId = null,
  anchorApplicationName = null,
}) => {
  const lightTheme = true;
  const { setAiProcessing } = useAiJobState();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState(null);
  const [fileProcessingStatus, setFileProcessingStatus] = useState({}); // Track individual file status
  const [jobId, setJobId] = useState(null);
  const [hasActiveBatch, setHasActiveBatch] = useState(false);
  const [isQueued, setIsQueued] = useState(false); // Track if file is queued for processing
  const [infoMessage, setInfoMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState([]); // Track validation errors for selected files

  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Supported file types (must match backend: src/file_processor.py)
  const supportedTypes = [
    ".txt",
    ".md",
    ".markdown",
    ".docx",
    ".pdf",
    ".png",
    ".jpg",
    ".jpeg",
    ".gif",
    ".webp",
  ];
  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const maxFiles = 25; // Practical batch size (LLM $ quota gates spend)

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileSelect = (files) => {
    if (isProcessing || hasActiveBatch || disabled) {
      onError?.(
        "A file processing job is already running. Please wait until it completes."
      );
      return;
    }

    if (files.length > maxFiles) {
      onError?.(
        `You can upload up to ${maxFiles} files at a time. Only the first ${maxFiles} will be kept.`
      );
      files = files.slice(0, maxFiles);
    }

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      // Validate file type
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));
      if (!supportedTypes.includes(fileExtension)) {
        const errorMsg = `${file.name}: Unsupported file type. Supported formats: ${supportedTypes.join(", ")}`;
        errors.push(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        const errorMsg = `${file.name}: File too large. Maximum size: ${(maxFileSize / (1024 * 1024)).toFixed(1)}MB`;
        errors.push(errorMsg);
        onError?.(errorMsg);
        return;
      }

      validFiles.push(file);
    });

    // Update validation errors state
    setValidationErrors(errors);

    // Only add valid files to selectedFiles
    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      if (errors.length === 0) {
        setValidationErrors([]);
      }
    } else {
      setSelectedFiles([]);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files);
  }, [onError, isProcessing, hasActiveBatch, disabled, selectedFiles]);

  const removeFile = (index) => {
    const fileToRemove = selectedFiles[index];
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setValidationErrors([]); // Clear validation errors when file is removed

    // Clean up the file status
    if (fileToRemove) {
      setFileProcessingStatus((prev) => {
        const newStatus = { ...prev };
        delete newStatus[fileToRemove.name];
        return newStatus;
      });
    }
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setProcessingProgress(0);
    setCurrentFile(null);
    setFileProcessingStatus({});
    setValidationErrors([]); // Clear validation errors
    setIsQueued(false); // Reset queued state
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return "⏳";
      case "processing":
        return "🔄";
      case "completed":
        return "✅";
      case "error":
        return "❌";
      default:
        return "📄";
    }
  };

  const getFileStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "text-gray-400";
      case "processing":
        return "text-blue-400";
      case "completed":
        return "text-green-400";
      case "error":
        return "text-red-400";
      default:
        return "text-gray-300";
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await apiFetch("/api/upload-file", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "File upload failed");
    }

    const result = await response.json();
    return {
      filename: result.file_info.filename,
      content: result.extracted_text,
      fileType: result.file_info.file_type,
      parserVersion: result.file_info.parser_version,
      fileBlobId: result.file_info.file_blob_id,
      originalFilename:
        result.file_info.original_filename || result.file_info.filename,
      fileSize: result.file_info.file_size_mb,
    };
  };

  const processFiles = async () => {
    if (selectedFiles.length === 0) return;

    // Prevent concurrent processing
    if (isProcessing || hasActiveBatch) {
      onError?.(
        "A file processing job is already running. Please wait until it completes."
      );
      return;
    }

    setIsProcessing(true);
    setProcessingProgress(0);
    setCurrentFile(null);
    // Initialize file processing status
    const initialStatus = {};
    selectedFiles.forEach((file) => {
      initialStatus[file.name] = {
        status: "pending",
        progress: 0,
        error: null,
      };
    });
    setFileProcessingStatus(initialStatus);

    try {
      await processApplicationEnrichmentFiles();
    } catch (error) {
      debugError("File processing error:", error);
      
      // Check if it's an unsupported file format error from backend
      if (
        error.message.includes("Unsupported file format") ||
        error.message.includes("unsupported") ||
        error.message.includes("Supported formats")
      ) {
        const errorMsg = `❌ ${error.message}. Please select a valid file. Supported formats: ${supportedTypes.join(", ")}`;
        setValidationErrors([errorMsg]);
        setSelectedFiles([]); // Clear invalid file selection
        onError?.(errorMsg);
      }
      // Check if it's an authentication error
      else if (
        error.message.includes("401") ||
        error.message.includes("unauthorized") ||
        error.message.includes("Missing or invalid authorization")
      ) {
        onError?.("Authentication required. Please log in and try again.");
      } else {
        onError?.(error.message);
      }
    } finally {
      // Keep processing state if a background batch/job is active
      setCurrentFile(null);
      setIsProcessing((prev) => (hasActiveBatch ? true : false));
    }
  };

  const processApplicationEnrichmentFiles = async () => {
    let provisionalId = null;
    try {
    const fileCount = selectedFiles.length;

    if (isLlmQuotaExhausted()) {
      onError?.(LLM_QUOTA_EXHAUSTED_MESSAGE);
      setIsProcessing(false);
      return;
    }

    const token = localStorage.getItem("access_token");
    if (!token) {
      throw new Error("Authentication required. Please log in and try again.");
    }

    const anchorId = String(
      knowledgeAnchor?.id || anchorApplicationId || ""
    ).trim();
    if (!anchorId) {
      onError?.(
        "Select where this knowledge belongs before uploading (go back to the anchor step)."
      );
      return;
    }
    const effectiveAnchor = knowledgeAnchor || {
      scopeType: "entity",
      id: anchorId,
      name: anchorApplicationName || "Application",
      extractMode: "auto",
    };
    const anchorLabel = effectiveAnchor.name || anchorApplicationName || "Knowledge";
    provisionalId = beginOptimisticEnrichmentQueue({
      anchorName: anchorLabel,
      anchorApplicationId: anchorId,
    });
    try {
      setAiProcessing(true);
    } catch {}

    const uploadResults = [];
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setCurrentFile(file.name);
      setProcessingProgress(((i + 0.5) / selectedFiles.length) * 50);
      setFileProcessingStatus((prev) => ({
        ...prev,
        [file.name]: { status: "processing", progress: 0, error: null },
      }));

      try {
        const result = await uploadFile(file);
        uploadResults.push(result);
        setFileProcessingStatus((prev) => ({
          ...prev,
          [file.name]: { status: "completed", progress: 100, error: null },
        }));
      } catch (error) {
        debugError(`Failed to extract text from ${file.name}:`, error);
        setFileProcessingStatus((prev) => ({
          ...prev,
          [file.name]: {
            status: "error",
            progress: 0,
            error: error.message,
          },
        }));
        throw error;
      }
    }

    const sources = uploadResults
      .filter((r) => r.content && !r.error)
      .map((r) => {
        const source = {
          kind: "document",
          label: `Add knowledge — ${r.filename}`,
          text: r.content,
        };
        if (r.parserVersion) {
          source.parser_version = r.parserVersion;
        }
        if (r.fileType) {
          source.file_type = r.fileType;
        }
        if (r.fileBlobId) {
          source.file_blob_id = r.fileBlobId;
        }
        if (r.originalFilename || r.filename) {
          source.original_filename = r.originalFilename || r.filename;
        }
        return source;
      });

    if (!sources.length) {
      throw new Error("No text could be extracted from the uploaded file(s).");
    }

    setProcessingProgress(75);
    setInfoMessage(addKnowledgeQueueLabel(effectiveAnchor));

    let response;
    try {
      response = await queueAddKnowledgeJob(api, effectiveAnchor, sources);
    } catch (error) {
      if (isEnrichmentQueueTimeoutError(error)) {
        const timeoutJobId = finalizeOptimisticEnrichmentQueueOnTimeout(provisionalId, {
          anchorName: anchorLabel,
          anchorApplicationId: anchorId,
        });
        setJobId(timeoutJobId);
        setHasActiveBatch(true);
        setIsProcessing(true);
        setIsQueued(true);
        setInfoMessage(
          "Your enrichment request may still be queued. Check Insights Inbox for the job."
        );
        onBatchProcessingStarted?.({ jobId: timeoutJobId });
        return;
      }
      rollbackOptimisticEnrichmentQueue(provisionalId);
      throw error;
    }

    const effectiveJobId = finalizeOptimisticEnrichmentQueue(provisionalId, response, {
      anchorName: anchorLabel || response.matched_application_name,
      anchorApplicationId: anchorId,
    });

    setJobId(effectiveJobId);
    setProcessingProgress(100);
    setHasActiveBatch(true);
    setIsProcessing(true);
    setIsQueued(true);

    const resolvedAnchor =
      effectiveAnchor.name ||
      response.matched_application_name ||
      "Knowledge";
    setInfoMessage(addKnowledgeSuccessMessage(effectiveAnchor, response));

    onBatchProcessingStarted?.({ jobId: effectiveJobId });

    if (response.job_id) {
      pollJobProgress(effectiveJobId);
    }
    } catch (err) {
      rollbackOptimisticEnrichmentQueue(provisionalId);
      throw err;
    }
  };

  const pollJobProgress = async (jid) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiFetch(`/api/jobs/${jid}`);
        if (!response.ok) {
          if (response.status === 404) {
            // Job not found - stop polling and clear stale ID
            debugLog(
              `⚠️ Job ${jid} not found (404), stopping polling and clearing stale job ID`
            );
          } else {
            debugError(
              `❌ Job status check failed: ${response.status} for job ${jid}`
            );
          }
          clearInterval(pollInterval);
          try {
            localStorage.removeItem("activeJobId");
            setAiProcessing(false);
          } catch {}
          setHasActiveBatch(false);
          setIsProcessing(false);
          setInfoMessage(
            "Previous enrichment job was not found. You can upload again."
          );
          return;
        }
        const status = await response.json();
        if (
          String(status?.job_type || "").toLowerCase() !== "application_enrichment" &&
          String(status?.job_type || "").toLowerCase() !== "knowledge_ingestion"
        ) {
          clearInterval(pollInterval);
          localStorage.removeItem("activeJobId");
          setHasActiveBatch(false);
          setIsProcessing(false);
          setIsQueued(false);
          setAiProcessing(false);
          return;
        }
        debugLog(
          `🔄 MultiFileUpload: Job ${jid} status:`,
          status.status,
          "progress:",
          status.progress
        );
        // Update progress if provided
        if (typeof status.progress === "number") {
          setProcessingProgress(status.progress);
        }
        // Terminal states
        if (status.status === "completed" || status.status === "failed") {
          debugLog(
            "🔄 MultiFileUpload: Job completed with status:",
            status.status
          );
          debugLog("🔄 MultiFileUpload: Job result:", status.result);
          clearInterval(pollInterval);
          try {
            localStorage.removeItem("activeJobId");
            setAiProcessing(false);
          } catch {}
          setHasActiveBatch(false);
          setIsProcessing(false);
          setIsQueued(false);
          if (status.status === "completed") {
            const indexedOnly =
              String(status?.job_type || "").toLowerCase() ===
              "knowledge_ingestion";
            setInfoMessage(
              indexedOnly
                ? "Indexing finished. Open Inbox to confirm, then check Sources on the entity."
                : "Enrichment finished. Open Insights Inbox to review extracted items."
            );
            onFilesProcessed?.([]);
          } else {
            setInfoMessage(
              status.error_message ||
                "Enrichment job failed. Try again or check Insights Inbox."
            );
          }
        }
      } catch (error) {
        debugError("Error polling job status:", error);
        clearInterval(pollInterval);
        try {
          localStorage.removeItem("activeJobId");
          setAiProcessing(false);
        } catch {}
        setHasActiveBatch(false);
        setIsProcessing(false);
      }
    }, 1000);
  };

  // Resume polling if user navigates away and returns
  useEffect(() => {
    try {
      const existingJob = localStorage.getItem("activeJobId");
      if (!existingJob) return;

      const validateAndResume = async () => {
        try {
          const response = await apiFetch(`/api/jobs/${existingJob}`);
          if (!response.ok) {
            debugLog(
              `⚠️ Stale job ID ${existingJob} not found, clearing from localStorage`
            );
            localStorage.removeItem("activeJobId");
            setHasActiveBatch(false);
            setIsProcessing(false);
            return;
          }
          const status = await response.json();
          const jt = String(status?.job_type || "").toLowerCase();
          if (jt !== "application_enrichment" && jt !== "knowledge_ingestion") {
            localStorage.removeItem("activeJobId");
            return;
          }
          setJobId(existingJob);
          setHasActiveBatch(true);
          setIsProcessing(true);
          setIsQueued(true);
          setInfoMessage(
            jt === "knowledge_ingestion"
              ? "Indexing is in progress. Inbox will confirm when it finishes."
              : "Application enrichment is in progress. Open Insights Inbox when review is ready."
          );
          pollJobProgress(existingJob);
        } catch (error) {
          debugError("Error validating job on resume:", error);
          localStorage.removeItem("activeJobId");
          setHasActiveBatch(false);
          setIsProcessing(false);
        }
      };
      validateAndResume();
    } catch {}
  }, []);

  return (
    <>
      {/* Queued Processing Screen - Similar to Text Processing */}
      {isQueued && (
        <div className="space-y-4 mb-6">
          <div
            className={
              lightTheme
                ? "rounded-xl border border-blue-200 bg-blue-50 p-6 text-center"
                : "bg-green-900/20 rounded-lg p-6 border border-green-700/50 text-center"
            }
          >
            <div className="text-3xl mb-3">⏳</div>
            <h3
              className={
                lightTheme
                  ? "text-lg font-semibold text-blue-900 mb-3"
                  : "text-lg font-semibold text-green-300 mb-3"
              }
            >
              Processing Queued
            </h3>
            <p
              className={
                lightTheme
                  ? "text-sm text-blue-800 mb-6 leading-relaxed"
                  : "text-green-200 text-sm mb-6"
              }
            >
              {infoMessage ||
                "Application enrichment is running in the background. You can continue working—open Insights Inbox when review is ready."}
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => {
                  setIsQueued(false);
                  setSelectedFiles([]);
                  setHasActiveBatch(false);
                  setIsProcessing(false);
                }}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                🔄 Add another source
              </button>
              {onNavigateToInbox && (
                <button
                  onClick={() => onNavigateToInbox()}
                  className="blink-soft-slow px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Open Insights Inbox
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {(isProcessing || hasActiveBatch) && !isQueued && (
        <div
          className={
            lightTheme
              ? "mb-3 p-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 text-sm"
              : "mb-3 p-3 bg-blue-900/30 border border-blue-700 rounded text-blue-200 text-sm"
          }
        >
          {infoMessage ||
            "Application enrichment is in progress. You can continue working—open Insights Inbox when review is ready."}
        </div>
      )}

      {!isQueued && selectedFiles.length === 0 && (
      <div
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors ${
          isDragOver
            ? "border-blue-400 bg-blue-900/20"
            : "border-gray-600 hover:border-gray-500"
        } ${disabled || isProcessing || hasActiveBatch ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        onClick={() =>
          !(disabled || isProcessing || hasActiveBatch) &&
          fileInputRef.current?.click()
        }
      >
        <div className="text-gray-400 mb-2">
          <svg
            className="mx-auto h-10 w-10"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="text-white font-medium mb-1">
          Drop files here or click to browse
        </p>
        <p className="text-sm text-gray-400 mb-2">
          Supported formats: {supportedTypes.join(", ")} • {formatFileSize(maxFileSize)} per file • up to {maxFiles} files
        </p>
      </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={supportedTypes.join(",")}
        onChange={(e) => handleFileSelect(Array.from(e.target.files || []))}
        className="hidden"
        disabled={disabled || isProcessing || hasActiveBatch}
      />

      {selectedFiles.length > 0 && !isQueued && (
        <div className="space-y-3">
          {selectedFiles.map((file, index) => {
            const fileStatus = fileProcessingStatus[file.name] || {
              status: "pending",
              progress: 0,
              error: null,
            };
            return (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <span className={`text-sm ${getFileStatusColor(fileStatus.status)}`}>
                    {getFileStatusIcon(fileStatus.status)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800">
                      {file.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatFileSize(file.size)}
                    </p>
                    {fileStatus.status === "processing" && (
                      <div className="mt-1 h-1 w-full rounded-full bg-slate-200">
                        <div
                          className="h-1 rounded-full bg-blue-600 transition-all duration-300"
                          style={{ width: `${fileStatus.progress}%` }}
                        />
                      </div>
                    )}
                    {fileStatus.status === "error" && fileStatus.error && (
                      <p className="mt-1 truncate text-xs text-red-600">
                        {fileStatus.error}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  disabled={isProcessing || hasActiveBatch}
                  className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      )}

      {isProcessing && (
        <div className="mt-4 p-4 bg-blue-100 rounded-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-blue-800">
              {currentFile
                ? `Processing: ${currentFile}`
                : "Processing files..."}{" "}
              ({Math.round(processingProgress)}%)
            </span>
          </div>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="mt-3 p-3 bg-red-900/20 border border-red-500 rounded-lg">
          <p className="text-red-400 text-sm font-medium mb-1">
            ⚠️ File Validation Error:
          </p>
          <ul className="text-red-300 text-sm list-disc list-inside">
            {validationErrors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
          <p className="text-red-400 text-xs mt-2">
            Please select a valid file to proceed. Supported formats: {supportedTypes.join(", ")}
          </p>
        </div>
      )}

      {selectedFiles.length > 0 && !isProcessing && !isQueued && (
        <div className="mt-4 flex flex-col items-stretch gap-2 border-t border-slate-200 pt-4 sm:items-end">
          {isLlmQuotaExhausted() ? (
            <div className="w-full p-3 bg-rose-50 border border-rose-200 rounded-lg">
              <p className="text-rose-900 text-sm">{LLM_QUOTA_EXHAUSTED_MESSAGE}</p>
            </div>
          ) : null}
          <button
            onClick={processFiles}
            disabled={
              validationErrors.length > 0 ||
              disabled ||
              isProcessing ||
              hasActiveBatch ||
              isLlmQuotaExhausted()
            }
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            title={validationErrors.length > 0 ? "Please fix validation errors before processing" : ""}
          >
            {selectedFiles.length > 1
              ? `Process ${selectedFiles.length} Files`
              : "Process File"}
          </button>
        </div>
      )}
    </>
  );
};

export default MultiFileUpload;
